import { useState, useEffect } from 'react';

const API = process.env.REACT_APP_API_URL || '';

const USER_TYPES = [
  { key: 'org_superadmin',     label: 'Org Superadmin',     desc: 'Full control of the org. Add/remove users, departments, contracts, and all data. Treat as root access.', requiresDept: false, isComplianceLevel: true },
  { key: 'compliance_officer', label: 'Compliance Officer', desc: 'Org-wide compliance lead. Creates memos, CAPs, policies. Sees every department.', requiresDept: false, isComplianceLevel: true },
  { key: 'compliance_analyst', label: 'Compliance Analyst', desc: 'Org-wide compliance team. Writes risks, audits, issues. Cannot create memos or CAPs.', requiresDept: false, isComplianceLevel: true },
  { key: 'department_lead',    label: 'Department Lead',    desc: 'Head of a single department. Full control of dept data only. Cannot see Privacy, FDR, PPM, Mandate, RuleIQ, Network Adequacy.', requiresDept: true, isComplianceLevel: false },
  { key: 'department_user',    label: 'Department User',    desc: 'Frontline staff in a single department. Read/write own dept records.', requiresDept: true, isComplianceLevel: false },
];

/**
 * PersonModal — single form for both adding and editing a user.
 *
 * Add mode: when person prop is empty object {}
 * Edit mode: when person has an id
 *
 * actorUserType is the CURRENTLY-LOGGED-IN user's type. Used purely for UX
 * (hiding user_type options the actor isn't allowed to assign). The server's
 * userTypeAssignmentError() in adminV2.js is the authoritative SOC 2 gate.
 */
export default function PersonModal({ person, orgId, departments, onClose, onSaved, actorUserType }) {
  const isEdit = !!person?.id;

  // Filter the user_type list so a Compliance Officer doesn't see options
  // they can't actually assign (org_superadmin) and no one but Platform
  // Superuser sees platform_superuser. The server enforces the same rule.
  const allowedUserTypes = USER_TYPES.filter((t) => {
    if (t.key === 'platform_superuser') return actorUserType === 'platform_superuser';
    // 2026-08-24 role redesign: super users are assigned by the platform
    // super admin only (server enforces the same rule in adminV2).
    if (t.key === 'org_superadmin') {
      return actorUserType === 'platform_superuser';
    }
    return true;
  });

  const [name, setName] = useState(person?.name || '');
  const [email, setEmail] = useState(person?.email || '');
  const [userType, setUserType] = useState(person?.user_type || 'department_user');
  const [departmentId, setDepartmentId] = useState(person?.department_id || '');
  const [sendInvite, setSendInvite] = useState(true);
  const [initialPassword, setInitialPassword] = useState('');
  // "Also oversees" — additional department memberships (read widening only;
  // the primary department above stays the single source of role identity).
  const [alsoDeptIds, setAlsoDeptIds] = useState([]);
  const [existingMemberships, setExistingMemberships] = useState([]); // edit mode: [{id, department_id}]

  // Edit mode: load the person's current extra departments.
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    fetch(`${API}/api/user-departments/${person.id}`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (!cancelled && d?.success) {
          setExistingMemberships(d.memberships || []);
          setAlsoDeptIds((d.memberships || []).map(m => m.department_id));
        }
      })
      .catch(() => { /* section still usable for adding */ });
    return () => { cancelled = true; };
  }, [isEdit, person?.id]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // null = status not loaded yet → the contract guard stays OFF (fail open, so
  // a load failure can never block legitimate onboarding).
  const [mappedDeptIds, setMappedDeptIds] = useState(null);

  // When user_type changes, clear dept if not required
  useEffect(() => {
    const def = USER_TYPES.find(t => t.key === userType);
    if (def && !def.requiresDept) setDepartmentId('');
  }, [userType]);

  // Load which departments already have a contract/PBP mapping. Read-only; if it
  // fails we leave mappedDeptIds null and the guard simply doesn't engage.
  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/departments/contract-mapping-status`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (!cancelled && d && d.success) setMappedDeptIds(d.mappedDepartmentIds || []); })
      .catch(() => { /* fail open — guard stays off */ });
    return () => { cancelled = true; };
  }, []);

  const selectedType = USER_TYPES.find(t => t.key === userType);

  // True ONLY when we know the chosen department has no contract mapping. If the
  // status hasn't loaded (mappedDeptIds === null), this stays false → fail open.
  const deptNeedsContract =
    !!selectedType?.requiresDept &&
    !!departmentId &&
    Array.isArray(mappedDeptIds) &&
    !mappedDeptIds.includes(departmentId);

  const handleSubmit = async () => {
    setError(null);

    if (!name.trim()) return setError('Name is required');
    if (!email.trim()) return setError('Email is required');
    if (!/^\S+@\S+\.\S+$/.test(email)) return setError('Invalid email format');
    if (selectedType?.requiresDept && !departmentId) {
      return setError(`${selectedType.label} requires a department`);
    }
    if (!isEdit && !sendInvite && (!initialPassword || initialPassword.length < 8)) {
      return setError('When not sending an invite, you must set an initial password (8+ chars)');
    }

    setSubmitting(true);
    try {
      let res, data;
      const wantedAlso = selectedType?.requiresDept ? alsoDeptIds.filter(d => d && d !== departmentId) : [];
      if (isEdit) {
        res = await fetch(`${API}/api/admin/v2/users/${person.id}`, {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name, user_type: userType, department_id: departmentId || null }),
        });
        data = await res.json();
        if (data.success) {
          // Reconcile "also oversees": add new memberships, remove unticked
          // ones. Each call is individually audited server-side.
          const have = new Map(existingMemberships.map(m => [m.department_id, m.id]));
          for (const d of wantedAlso) {
            if (!have.has(d)) {
              await fetch(`${API}/api/user-departments`, {
                method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_id: person.id, department_id: d }),
              }).catch(() => {});
            }
          }
          for (const [deptId, membershipId] of have) {
            if (!wantedAlso.includes(deptId)) {
              await fetch(`${API}/api/user-departments/${membershipId}`, {
                method: 'DELETE', credentials: 'include',
              }).catch(() => {});
            }
          }
          onSaved('update', data.user, data);
        } else setError(data.message || 'Failed to update');
      } else {
        res = await fetch(`${API}/api/admin/v2/orgs/${orgId}/users`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name,
            email,
            user_type: userType,
            department_id: departmentId || null,
            additional_department_ids: wantedAlso,
            send_invite: sendInvite,
            initial_password: sendInvite ? null : initialPassword,
          }),
        });
        data = await res.json();
        if (data.success) onSaved('create', data.user, data);
        else setError(data.message || 'Failed to create user');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="relative bg-slate-900 border border-slate-700/50 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-700/50 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold text-white">
              {isEdit ? 'Edit Person' : 'Add Person'}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {isEdit ? `Update ${person.name}'s role and access` : 'Create a new team member and send a welcome email'}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-2xl leading-none w-8 h-8 flex items-center justify-center">×</button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 overflow-y-auto flex-1 space-y-4">
          {/* Name */}
          <Field label="Full Name *">
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Sarah Chen"
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none transition"
            />
          </Field>

          {/* Email — readonly when editing */}
          <Field label="Email *">
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              disabled={isEdit}
              placeholder="sarah@org.com"
              className={`w-full border rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none transition ${
                isEdit
                  ? 'bg-slate-800/40 border-slate-700/50 cursor-not-allowed'
                  : 'bg-slate-800 border-slate-700 focus:border-blue-500'
              }`}
            />
            {isEdit && <p className="text-[11px] text-slate-500 mt-1">Email cannot be changed.</p>}
          </Field>

          {/* User type */}
          <Field label="User Type *">
            <div className="space-y-1.5">
              {allowedUserTypes.map(t => (
                <label
                  key={t.key}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition ${
                    userType === t.key
                      ? 'bg-blue-500/10 border-blue-500/40'
                      : 'bg-slate-800/40 border-slate-700/60 hover:border-slate-600'
                  }`}
                >
                  <input
                    type="radio"
                    name="user_type"
                    value={t.key}
                    checked={userType === t.key}
                    onChange={() => setUserType(t.key)}
                    className="mt-0.5 text-blue-500 focus:ring-blue-500"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-white">{t.label}</p>
                      {t.isComplianceLevel && (
                        <span className="text-[9px] bg-emerald-500/15 text-emerald-300 px-1.5 py-0.5 rounded uppercase font-bold tracking-wide">Org-wide</span>
                      )}
                      {t.requiresDept && (
                        <span className="text-[9px] bg-amber-500/15 text-amber-300 px-1.5 py-0.5 rounded uppercase font-bold tracking-wide">Dept-bound</span>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">{t.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </Field>

          {/* Department selector — only when required */}
          {selectedType?.requiresDept && (
            <Field label="Department *">
              {departments.length === 0 ? (
                // SOC 2 onboarding UX: don't silently dead-end. A dept-bound
                // user_type requires a department, so guide the operator to
                // create one first. The People page already has a Departments
                // tab (added 2026-05-17) for inline create + rename + aliases.
                <div className="bg-amber-500/10 border border-amber-500/40 rounded-lg px-4 py-3 text-sm">
                  <p className="text-amber-200 font-medium mb-1">No departments yet in this org</p>
                  <p className="text-amber-100/80 text-xs mb-3">
                    {selectedType.label} accounts must belong to a department — that's how Mandate routes
                    regulatory memos to the right team. Add at least one department first.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      // Closing the modal returns the operator to /people; the
                      // Departments tab is one click away. We don't auto-switch
                      // tabs from here because PersonModal is decoupled from
                      // the parent's tab state — that's a deliberate boundary.
                      onClose?.();
                    }}
                    className="px-3 py-1.5 bg-amber-500/30 hover:bg-amber-500/50 border border-amber-400/60 rounded-md text-xs font-semibold text-white transition"
                  >
                    Close and open the Departments tab →
                  </button>
                </div>
              ) : (
                <select
                  value={departmentId}
                  onChange={e => setDepartmentId(e.target.value)}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:border-blue-500 focus:outline-none transition"
                >
                  <option value="">Select a department…</option>
                  {departments.map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              )}
            </Field>
          )}

          {/* Also oversees — extra departments for someone supervising several
              areas (e.g., a Medical Officer over UM, Credentialing, and Case
              Management). Read visibility only; role and primary department
              stay exactly as chosen above. */}
          {selectedType?.requiresDept && departmentId && departments.length > 1 && (
            <Field label="Also oversees (optional)">
              <p className="text-[11px] text-slate-500 mb-2 leading-snug">
                Extra departments this person sees across the platform — their items, policies, and reviews. This never grants compliance-level access, and Mandate memo routing is unaffected.
              </p>
              <div className="flex flex-wrap gap-1.5">
                {departments.filter(d => d.id !== departmentId).map(d => {
                  const on = alsoDeptIds.includes(d.id);
                  return (
                    <button key={d.id} type="button"
                      onClick={() => setAlsoDeptIds(prev => on ? prev.filter(x => x !== d.id) : [...prev, d.id])}
                      className={`text-xs px-2.5 py-1 rounded-full border transition ${
                        on ? 'bg-blue-500/20 border-blue-400 text-blue-200' : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-500'
                      }`}>
                      {d.name}
                    </button>
                  );
                })}
              </div>
            </Field>
          )}

          {/* Order guard: the chosen department has no contract/plan mapping yet,
              so a dept-bound user added now would get NO Mandate access. Steer the
              admin to map contracts first. Mirrors the no-departments guard above. */}
          {deptNeedsContract && (
            <div className="bg-amber-500/10 border border-amber-500/40 rounded-lg px-4 py-3 text-sm">
              <p className="text-amber-200 font-medium mb-1">This department isn&apos;t mapped to a contract yet</p>
              <p className="text-amber-100/80 text-xs">
                A {selectedType.label} only gets Mandate access once their department is linked to a
                contract/plan. Map it first (Departments tab → Map Contracts), then add this person —
                otherwise they&apos;ll sign in with no access.
              </p>
            </div>
          )}

          {/* Send invite vs set password — only on create */}
          {!isEdit && (
            <div className="bg-slate-800/30 border border-slate-700/40 rounded-lg p-3">
              <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-300 mb-2">
                <input
                  type="checkbox"
                  checked={sendInvite}
                  onChange={() => setSendInvite(v => !v)}
                  className="rounded border-slate-600 bg-slate-800 text-blue-500 focus:ring-blue-500"
                />
                Send welcome email with set-password link
              </label>
              {!sendInvite && (
                <Field label="Initial Password" extraClass="mt-2">
                  <input
                    type="password"
                    value={initialPassword}
                    onChange={e => setInitialPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-sm text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
                  />
                </Field>
              )}
            </div>
          )}

          {/* Live preview */}
          <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-3">
            <p className="text-[11px] uppercase tracking-wide text-blue-400 font-semibold mb-2">Access preview</p>
            <PreviewBlock userType={userType} departments={departments} departmentId={departmentId} alsoDeptIds={alsoDeptIds} />
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded-lg px-4 py-3">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-700/50 flex items-center justify-between flex-shrink-0">
          <button onClick={onClose} className="px-4 py-2 text-slate-400 hover:text-white text-sm transition">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={
              submitting
              || !name.trim()
              || !email.trim()
              // SOC 2 onboarding UX: refuse to submit a dept-bound user when
              // the org has zero departments. The error message above is
              // already shown; disabling the button stops a confusing 400
              // round-trip.
              || (selectedType?.requiresDept && departments.length === 0)
              // Don't let a dept-bound user be added to a department with no
              // contract mapping yet — they'd get no Mandate access.
              || deptNeedsContract
            }
            className="px-5 py-2 bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg font-medium text-sm text-white disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg hover:shadow-blue-500/25 transition"
          >
            {submitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Person'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, extraClass = '', children }) {
  return (
    <div className={extraClass}>
      <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function PreviewBlock({ userType, departments, departmentId, alsoDeptIds = [] }) {
  const def = USER_TYPES.find(t => t.key === userType);
  const dept = departments.find(d => d.id === departmentId);
  const alsoNames = alsoDeptIds
    .filter(id => id !== departmentId)
    .map(id => departments.find(d => d.id === id)?.name)
    .filter(Boolean);

  if (!def) return <p className="text-xs text-slate-500">Select a type to see access preview.</p>;

  return (
    <div className="space-y-1 text-xs text-slate-300">
      <div className="flex items-start gap-2">
        <span className="text-emerald-400 flex-shrink-0">✓</span>
        <span>
          <strong className="text-white">{def.label}</strong>
          {def.requiresDept && dept && <> in <strong className="text-amber-300">{dept.name}</strong></>}
          {def.requiresDept && !dept && <span className="text-amber-400"> (department required)</span>}
        </span>
      </div>
      <div className="flex items-start gap-2">
        <span className="text-emerald-400 flex-shrink-0">✓</span>
        <span>{def.isComplianceLevel ? 'Sees data across ALL departments' : 'Sees data ONLY for their department'}</span>
      </div>
      {!def.isComplianceLevel && alsoNames.length > 0 && (
        <div className="flex items-start gap-2">
          <span className="text-emerald-400 flex-shrink-0">✓</span>
          <span>Also sees <strong className="text-amber-300">{alsoNames.join(', ')}</strong> (items, policies, reviews)</span>
        </div>
      )}
      {def.isComplianceLevel ? (
        <div className="flex items-start gap-2">
          <span className="text-emerald-400 flex-shrink-0">✓</span>
          <span>Can access compliance-only apps (Privacy, FDR, PPM, Mandate, RuleIQ, NA)</span>
        </div>
      ) : (
        <div className="flex items-start gap-2">
          <span className="text-red-400 flex-shrink-0">✗</span>
          <span>Cannot access Privacy, FDR, PPM, Mandate, RuleIQ, Network Adequacy</span>
        </div>
      )}
      {userType === 'compliance_analyst' && (
        <div className="flex items-start gap-2">
          <span className="text-red-400 flex-shrink-0">✗</span>
          <span>Cannot create memos or CAPs (read only)</span>
        </div>
      )}
      {(userType === 'department_lead' || userType === 'department_user') && (
        <div className="flex items-start gap-2">
          <span className="text-red-400 flex-shrink-0">✗</span>
          <span>Cannot create memos or CAPs</span>
        </div>
      )}
    </div>
  );
}
